// [PURPOSE]: Canonical D&D 5e conditions data — extracted from
//   ConditionsTracker.js so the picker, EffectBuilder, custom-condition
//   creator, and party-sync layer all reference one source of truth.
// [WHY]: Phase B / #027 (2026-05-06). Pre-extraction the 13 conditions
//   lived inline in `src/components/ConditionsTracker.js` as a private
//   const. Bucket 1's B1-6 (condition cause + DC effect type) needs to
//   read condition ids from data — embedding those names in the
//   picker would re-create the string-domain anti-pattern that Rule
//   #11 / `feedback_never_use_strings.md` exists to prevent. Promoting
//   to a shared module also matches the `srdFeats.js` / `srdSpells.js`
//   pattern and gives the eventual custom-condition-creator UI a
//   place to write to.
// [INTEGRATION]: Imported by `src/components/ConditionsTracker.js`
//   (consumes `SRD_CONDITIONS` for the active/available list +
//   `numericEffects` for effectRegistry integration), and as of #028
//   by `subclassEffectBuilderVocab.js` (the `causeCondition` effect
//   type's value picker).
//
// Schema:
//   - key             — stable id (snake_case lowercase)
//   - name            — display label
//   - severity        — beneficial | mild | moderate | severe | critical
//   - effects[]       — narrative effect strings (PHB wording)
//   - icon            — single emoji for the chip rendering
//   - color           — Tailwind palette key for the chip background
//   - numericEffects? — optional structured effects for the registry
//                       integration. See effectRegistry contract.
//   - autoDetected?   — true when the system auto-applies/clears the
//                       condition (e.g., Unconscious at HP=0).

export const SRD_CONDITIONS = Object.freeze({
  blinded: {
    name: 'Blinded',
    severity: 'moderate',
    effects: [
      'You can\'t see and automatically fail ability checks that require sight',
      'Attack rolls against you have advantage',
      'Your attack rolls have disadvantage',
    ],
    icon: '👁️',
    color: 'yellow',
  },
  charmed: {
    name: 'Charmed',
    severity: 'moderate',
    effects: [
      'You can\'t attack the charmer or target them with harmful abilities or magical effects',
      'The charmer has advantage on ability checks to interact socially with you',
    ],
    icon: '💕',
    color: 'pink',
  },
  deafened: {
    name: 'Deafened',
    severity: 'mild',
    effects: [
      'You can\'t hear and automatically fail ability checks that require hearing',
    ],
    icon: '🔇',
    color: 'gray',
  },
  frightened: {
    name: 'Frightened',
    severity: 'moderate',
    effects: [
      'You have disadvantage on ability checks and attack rolls while the source of fear is within line of sight',
      'You can\'t willingly move closer to the source of fear',
    ],
    icon: '😨',
    color: 'purple',
    // [Sub-plan 1Q-F-7b-ii 2026-05-28]: Disadvantage on all ability checks via registry.
    // PHB p.290: applies while source of fear is visible. Visibility gating is a Combat
    // Tracker dependency tracked at Sub-plan 1Q-F-7c; for now, applied unconservatively
    // (same behavior as legacy hasDisadvantageOnAbilityCheck hardcoded check).
    // [BREADCRUMB Rule 25 — Sub-plan 3B]: Custom Condition Creator will emit the same shape.
    numericEffects: [
      { type: 'check', target: 'all', operation: 'disadvantage', condition: 'always' },
    ],
  },
  grappled: {
    name: 'Grappled',
    severity: 'moderate',
    effects: [
      'Your speed becomes 0, and you can\'t benefit from any bonus to your speed',
      'The condition ends if the grappler is incapacitated',
      'The condition ends if you are removed from the grappler\'s reach',
    ],
    icon: '🤝',
    color: 'orange',
    numericEffects: [
      { type: 'speed', operation: 'set', value: 0, condition: 'always' },
    ],
  },
  incapacitated: {
    name: 'Incapacitated',
    severity: 'severe',
    effects: [
      'You can\'t take actions or reactions',
    ],
    icon: '🚫',
    color: 'red',
  },
  invisible: {
    name: 'Invisible',
    severity: 'beneficial',
    effects: [
      'You are impossible to see without the aid of magic or a special sense',
      'You are heavily obscured for the purpose of hiding',
      'Attack rolls against you have disadvantage',
      'Your attack rolls have advantage',
    ],
    icon: '👻',
    color: 'blue',
  },
  paralyzed: {
    name: 'Paralyzed',
    severity: 'severe',
    effects: [
      'You are incapacitated and can\'t move or speak',
      'You automatically fail Strength and Dexterity saving throws',
      'Attack rolls against you have advantage',
      'Any attack that hits you is a critical hit if the attacker is within 5 feet',
    ],
    icon: '🥶',
    color: 'red',
    numericEffects: [
      { type: 'save', target: 'strength', operation: 'autoFail', condition: 'always' },
      { type: 'save', target: 'dexterity', operation: 'autoFail', condition: 'always' },
      // [EMC-3 2026-07-04 — user ruling: STRICT RAW]: PHB p.291 auto-fails STR/DEX SAVING
      // THROWS only; ability checks roll normally. The 1Q-F-7b-ii type:'check' autoFail rows
      // were removed; the check-autoFail machinery (registry 'check:<ability>' routing +
      // hasAutoFailCheck reader) stays for CustomConditionCreator-authored conditions.
      { type: 'attacksAgainst', operation: 'advantage', condition: 'always' },
      {
        type: 'attacksAgainstCrit',
        operation: 'autoSuccess',
        condition: {
          when: 'attackerWithin5ft',
          check: (context) => (context?.distance || Infinity) <= 5,
        },
      },
    ],
  },
  petrified: {
    name: 'Petrified',
    severity: 'severe',
    effects: [
      'You are transformed into a solid inanimate substance (usually stone)',
      'Your weight increases by a factor of ten, and you cease aging',
      'You are incapacitated, can\'t move or speak, and are unaware of your surroundings',
      'Attack rolls against you have advantage',
      'You automatically fail Strength and Dexterity saving throws',
      'You have resistance to all damage',
      'You are immune to poison and disease (existing poisons/diseases are suspended)',
    ],
    icon: '🗿',
    color: 'red',
    numericEffects: [
      { type: 'save', target: 'strength', operation: 'autoFail', condition: 'always' },
      { type: 'save', target: 'dexterity', operation: 'autoFail', condition: 'always' },
      // [EMC-3 2026-07-04 — user ruling: STRICT RAW]: auto-fail = STR/DEX SAVING THROWS only
      // (PHB pp.291-292); the 1Q-F-7b-ii type:'check' autoFail rows were removed.
      { type: 'attacksAgainst', operation: 'advantage', condition: 'always' },
      // [Sub-plan 1Q — 2026-05-29]: Expanded from value:'all' sentinel (which was silently inert —
      // effectCalculator.getDamageResistances adds effect.value as a literal string; it never understood 'all').
      // PHB 2014 p.291 — Petrified: resistance to ALL damage with no exclusions (includes psychic).
      // Values match DAMAGE_TYPES enum (src/constants/enums.js). BREADCRUMB resolved — no reader change needed.
      { type: 'resistance', value: 'acid', operation: 'grant', condition: 'always' },
      { type: 'resistance', value: 'bludgeoning', operation: 'grant', condition: 'always' },
      { type: 'resistance', value: 'cold', operation: 'grant', condition: 'always' },
      { type: 'resistance', value: 'fire', operation: 'grant', condition: 'always' },
      { type: 'resistance', value: 'force', operation: 'grant', condition: 'always' },
      { type: 'resistance', value: 'lightning', operation: 'grant', condition: 'always' },
      { type: 'resistance', value: 'necrotic', operation: 'grant', condition: 'always' },
      { type: 'resistance', value: 'piercing', operation: 'grant', condition: 'always' },
      { type: 'resistance', value: 'poison', operation: 'grant', condition: 'always' },
      { type: 'resistance', value: 'psychic', operation: 'grant', condition: 'always' },
      { type: 'resistance', value: 'radiant', operation: 'grant', condition: 'always' },
      { type: 'resistance', value: 'slashing', operation: 'grant', condition: 'always' },
      { type: 'resistance', value: 'thunder', operation: 'grant', condition: 'always' },
    ],
  },
  poisoned: {
    name: 'Poisoned',
    severity: 'moderate',
    effects: [
      'You have disadvantage on attack rolls and ability checks',
    ],
    icon: '🤢',
    color: 'green',
    // [Sub-plan 1Q-F-7b-ii 2026-05-28]: Disadvantage on all ability checks via registry.
    // PHB p.292: poisoned creature has disadvantage on attack rolls AND ability checks.
    // [BREADCRUMB Rule 25 — Sub-plan 3B]: Custom Condition Creator will emit the same shape.
    numericEffects: [
      { type: 'check', target: 'all', operation: 'disadvantage', condition: 'always' },
    ],
  },
  prone: {
    name: 'Prone',
    severity: 'mild',
    effects: [
      'Your only movement option is to crawl (costs 1 extra foot per foot moved)',
      'You have disadvantage on attack rolls',
      'Attack rolls against you have advantage if the attacker is within 5 feet',
      'Attack rolls against you have disadvantage if the attacker is more than 5 feet away',
      'Standing up costs half your movement',
    ],
    icon: '🤕',
    color: 'orange',
  },
  restrained: {
    name: 'Restrained',
    severity: 'severe',
    effects: [
      'Your speed becomes 0, and you can\'t benefit from any bonus to your speed',
      'Attack rolls against you have advantage',
      'Your attack rolls have disadvantage',
      'You have disadvantage on Dexterity saving throws',
    ],
    icon: '⛓️',
    color: 'red',
    numericEffects: [
      { type: 'speed', operation: 'set', value: 0, condition: 'always' },
      { type: 'attacksAgainst', operation: 'advantage', condition: 'always' },
      { type: 'attackRoll', operation: 'disadvantage', condition: 'always' },
      { type: 'save', target: 'dexterity', operation: 'disadvantage', condition: 'always' },
    ],
  },
  stunned: {
    name: 'Stunned',
    severity: 'severe',
    effects: [
      'You are incapacitated, can\'t move, and can speak only falteringly',
      'You automatically fail Strength and Dexterity saving throws',
      'Attack rolls against you have advantage',
    ],
    icon: '💫',
    color: 'red',
    numericEffects: [
      { type: 'save', target: 'strength', operation: 'autoFail', condition: 'always' },
      { type: 'save', target: 'dexterity', operation: 'autoFail', condition: 'always' },
      // [EMC-3 2026-07-04 — user ruling: STRICT RAW]: auto-fail = STR/DEX SAVING THROWS only
      // (PHB pp.291-292); the 1Q-F-7b-ii type:'check' autoFail rows were removed.
      { type: 'attacksAgainst', operation: 'advantage', condition: 'always' },
    ],
  },
  unconscious: {
    name: 'Unconscious',
    severity: 'critical',
    effects: [
      'You are incapacitated, can\'t move or speak, and are unaware of your surroundings',
      'You drop whatever you\'re holding and fall prone',
      'You automatically fail Strength and Dexterity saving throws',
      'Attack rolls against you have advantage',
      'Any attack that hits you is a critical hit if the attacker is within 5 feet',
    ],
    icon: '😵',
    color: 'red',
    autoDetected: true,
    numericEffects: [
      { type: 'save', target: 'strength', operation: 'autoFail', condition: 'always' },
      { type: 'save', target: 'dexterity', operation: 'autoFail', condition: 'always' },
      // [EMC-3 2026-07-04 — user ruling: STRICT RAW]: PHB p.292 Unconscious auto-fails STR/DEX
      // SAVING THROWS only; ability checks are not auto-failed by RAW. The 1Q-F-7b-ii
      // all-abilities type:'check' autoFail row was removed.
      { type: 'attacksAgainst', operation: 'advantage', condition: 'always' },
      {
        type: 'attacksAgainstCrit',
        operation: 'autoSuccess',
        condition: {
          when: 'attackerWithin5ft',
          check: (context) => (context?.distance || Infinity) <= 5,
        },
      },
    ],
  },
});

/**
 * Stable id list for picker dropdowns. Sorted alphabetically by display name.
 * Used by the causeCondition effect type (B1-6) and any other UI that needs
 * to enumerate D&D 5e conditions.
 */
export const SRD_CONDITION_IDS = Object.freeze(
  Object.keys(SRD_CONDITIONS).sort((a, b) =>
    SRD_CONDITIONS[a].name.localeCompare(SRD_CONDITIONS[b].name)
  )
);

/**
 * Convenience option list for `<select>` pickers — `[{ value, label }, ...]`
 * sorted by display name.
 */
export const SRD_CONDITION_OPTIONS = Object.freeze(
  SRD_CONDITION_IDS.map(id => Object.freeze({
    value: id,
    label: SRD_CONDITIONS[id].name,
    icon: SRD_CONDITIONS[id].icon,
  }))
);
